/**
 * useParticipantFilters Hook
 * Filters and organizes friends and groups for participant selection
 */

import { useMemo } from 'react';
import { isInvalidName } from '../helpers/createUi.helper';
import type { Friend } from '../../friends/types/friends.types';
import type { Group } from '../../../types/challenges';

interface UseParticipantFiltersProps {
  friends: Friend[];
  availableGroups: Group[];
}

interface UseParticipantFiltersReturn {
  acceptedFriends: Friend[];
  pendingFriends: Friend[];
  validGroups: Group[];
}

export const useParticipantFilters = ({
  friends,
  availableGroups,
}: UseParticipantFiltersProps): UseParticipantFiltersReturn => {
  // Filter and organize friends by status
  const { acceptedFriends, pendingFriends } = useMemo(() => {
    const validFriends = friends.filter((f) => !isInvalidName(f.name));
    const accepted = validFriends.filter((f) => f.friendshipStatus === 'accepted');
    const pending = validFriends.filter((f) => f.friendshipStatus === 'pending');
    return { acceptedFriends: accepted, pendingFriends: pending };
  }, [friends]);

  // Filter valid groups (exclude Quick Challenge)
  const validGroups = useMemo(
    () => availableGroups.filter((group) => group && group.name && group.name !== 'Quick Challenge'),
    [availableGroups]
  );

  return {
    acceptedFriends,
    pendingFriends,
    validGroups,
  };
};

